import React, { useRef, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";

type GlowPosition = {
  right?: string;
  left?: string;
  top?: string;
  bottom?: string;
};

export default function HoloCard({
  children,
  className,
  enableGlow = true,
  defaultGlowPosition = { right: "-30px", top: "90%" },
}: {
  children: React.ReactNode;
  className?: string;
  enableGlow?: boolean;
  defaultGlowPosition?: GlowPosition;
}) {
  const cardRef = useRef<HTMLDivElement>(null);
  const glowRef = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!enableGlow) return;
    
    const card = cardRef.current;
    const glow = glowRef.current;
    if (!card || !glow) return;

    const rect = card.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    glow.style.left = `${x - glow.offsetWidth / 2}px`;
    glow.style.top = `${y - glow.offsetHeight / 2}px`;
    glow.style.opacity = "1";
  };

  const handleMouseLeave = () => {
    if (!enableGlow) return;
    
    const glow = glowRef.current;
    if (!glow) return;

    // Apply default position from props
    Object.entries(defaultGlowPosition).forEach(([key, value]: [string, string]) => {
      // Use a type assertion with unknown as an intermediate step
      (glow.style as unknown as Record<string, string>)[key] = value;
    });
    
    glow.style.opacity = "1";
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.3 }
    );

    const currentRef = cardRef.current;
    if (currentRef) observer.observe(currentRef);

    return () => {
      if (currentRef) observer.unobserve(currentRef);
    };
  }, []);

  // Apply initial glow position on mount
  useEffect(() => {
    if (!enableGlow) return;
    
    const glow = glowRef.current;
    if (!glow) return;
    
    // Reset any manually set positions
    glow.style.left = '';
    glow.style.right = '';
    glow.style.top = '';
    glow.style.bottom = '';
    
    // Apply default positions
    Object.entries(defaultGlowPosition).forEach(([key, value]) => {
      // Use the same pattern
      (glow.style as unknown as Record<string, string>)[key] = value;
    });
  }, [defaultGlowPosition, enableGlow]);

  return (
    <motion.div
      ref={cardRef}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      initial={{ opacity: 0, scale: 0.9 }}
      animate={
        isVisible ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }
      }
      transition={{ duration: 0.3, ease: "easeOut" }}
      className={cn(
        "relative w-full min-h-[26rem] mx-auto md:min-h-[18rem] overflow-hidden rounded-xl backdrop-blur-md bg-[#0200074a] drop-shadow-xl group p-0.5",
        className
      )}
    >
      <div className="z-[1] h-full flex flex-col items-start justify-start gap-6 rounded-xl bg-black/50 p-6">
        {children}
      </div>
      {enableGlow && (
        <div
          ref={glowRef}
          className="absolute h-48 w-56 -z-10 bg-[rgb(154,87,255)] blur-[50px] transition-all duration-200 pointer-events-none"
          style={defaultGlowPosition}
        ></div>
      )}
    </motion.div>
  );
}
